// SharedSecurity.jsx
import { useEffect, useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";

const SharedSecurity = () => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [controls, isInView]);

  const ourResponsibilities = [
    "Keep servers and systems secure",
    "Lock and protect your data",
    "Watch for problems 24/7",
    "Back up everything automatically",
    "Update and fix security issues",
  ];

  const yourResponsibilities = [
    "Use strong passwords and two-factor login",
    "Control who has access to what",
    "Check activity logs occasionally",
    "Teach your team about security",
    "Tell us if something seems wrong",
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
        duration: 0.6,
      },
    },
  };

  const listItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 80,
        duration: 0.5,
      },
    },
  };

  return (
    <section className="SharedSecurity px-6 py-20 lg:py-28 bg-gradient-to-b from-white via-[#093A5B]/[0.02] to-white relative overflow-hidden">
      {/* Animated Diagonal Lines Background */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.04]">
        <defs>
          <motion.line
            x1="10%"
            y1="20%"
            x2="90%"
            y2="80%"
            stroke="#093A5B"
            strokeWidth="2"
            strokeDasharray="5 5"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.5, delay: 0.5 }}
          />
          <motion.line
            x1="90%"
            y1="20%"
            x2="10%"
            y2="80%"
            stroke="#093A5B"
            strokeWidth="2"
            strokeDasharray="5 5"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.5, delay: 0.8 }}
          />
        </defs>
      </svg>

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#093A5B]/20 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              opacity: 0,
            }}
            animate={{
              y: [0, -100, 0],
              x: [0, (Math.random() - 0.5) * 50, 0],
              opacity: [0, 0.3, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 5 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 8,
              ease: "easeInOut",
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      <div className="mx-auto max-w-7xl relative z-10" ref={ref}>
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={controls}
          variants={{
            visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
          }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, rotate: -10 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{
              duration: 0.5,
              type: "spring",
              damping: 12,
              stiffness: 100,
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#093A5B]/10 to-[#093A5B]/5 border border-[#093A5B]/10 mb-6"
          >
            <motion.div
              animate={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4 text-[#093A5B]"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </motion.div>
            <span className="text-sm font-semibold text-[#093A5B]">
              Shared Responsibility
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-4xl lg:text-5xl font-bold text-black mb-6 leading-tight"
          >
            Security is a partnership
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto"
          >
            We provide a secure platform foundation while you manage access and
            user security within your workspace.
          </motion.p>
        </motion.div>

        {/* Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="grid lg:grid-cols-2 gap-8"
        >
          {/* Our Responsibilities Card */}
          <motion.div
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{
              scale: 1,
              y: -1,
            }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-gradient-to-br from-[#093A5B] to-[#093A5B]/80 rounded-3xl p-10 shadow-2xl overflow-hidden group"
          >
            {/* Animated Shimmer Effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
              animate={{
                x: ["-100%", "200%"],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "linear",
                delay: 1,
              }}
            />

            {/* Header */}
            <div className="relative flex items-center gap-4 mb-6">
              <motion.div
                className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center"
                whileHover={{
                  rotate: [0, -10, 10, 0],
                  scale: 1.1,
                }}
                transition={{ duration: 0.4 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-7 h-7 text-white"
                >
                  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                </svg>
              </motion.div>
              <h3 className="text-2xl font-bold text-white">
                Our responsibilities
              </h3>
            </div>

            <p className="relative text-white/90 mb-6 leading-relaxed">
              Simplifyte maintains secure infrastructure and operational
              safeguards.
            </p>

            {/* Responsibilities List */}
            <ul className="relative space-y-3">
              {ourResponsibilities.map((item, idx) => (
                <motion.li
                  key={idx}
                  variants={listItemVariants}
                  custom={idx}
                  className="flex items-start gap-3 group/item"
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-5 h-5 text-white flex-shrink-0 mt-0.5"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                  </motion.div>
                  <span className="text-white/90 group-hover/item:text-white transition-colors">
                    {item}
                  </span>
                </motion.li>
              ))}
            </ul>

            {/* Animated Bottom Border */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-white via-white/50 to-transparent"
              initial={{ scaleX: 0 }}
              whileHover={{ scaleX: 1 }}
              transition={{ duration: 0.4 }}
              style={{ transformOrigin: "left" }}
            />
          </motion.div>

          {/* Your Responsibilities Card */}
          <motion.div
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{
              scale: 1,
              y: -1,
            }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-white rounded-3xl p-10 border-2 border-[#093A5B]/20 shadow-xl overflow-hidden group"
          >
            {/* Pattern Background */}
            <motion.div
              className="absolute inset-0 opacity-5"
              animate={{
                backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                backgroundImage: `
                  linear-gradient(45deg, #093A5B 25%, transparent 25%, transparent 75%, #093A5B 75%, #093A5B),
                  linear-gradient(45deg, #093A5B 25%, transparent 25%, transparent 75%, #093A5B 75%, #093A5B)
                `,
                backgroundSize: "40px 40px",
              }}
            />

            {/* Header */}
            <div className="relative flex items-center gap-4 mb-6">
              <motion.div
                className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#093A5B]/10 to-[#093A5B]/5 flex items-center justify-center"
                whileHover={{
                  rotate: [0, -10, 10, 0],
                  scale: 1.1,
                }}
                transition={{ duration: 0.4 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-7 h-7 text-[#093A5B]"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </motion.div>
              <h3 className="text-2xl font-bold text-black">
                Your responsibilities
              </h3>
            </div>

            <p className="relative text-gray-600 mb-6 leading-relaxed">
              You control access and user security within your workspace.
            </p>

            {/* Responsibilities List */}
            <ul className="relative space-y-3">
              {yourResponsibilities.map((item, idx) => (
                <motion.li
                  key={idx}
                  variants={listItemVariants}
                  custom={idx}
                  className="flex items-start gap-3 group/item"
                >
                  <motion.div
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-5 h-5 text-[#093A5B] flex-shrink-0 mt-0.5"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                  </motion.div>
                  <span className="text-gray-700 group-hover/item:text-[#093A5B] transition-colors">
                    {item}
                  </span>
                </motion.li>
              ))}
            </ul>

            {/* Animated Bottom Border */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#093A5B] via-[#093A5B]/50 to-transparent"
              initial={{ scaleX: 0 }}
              whileHover={{ scaleX: 1 }}
              transition={{ duration: 0.4 }}
              style={{ transformOrigin: "left" }}
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        .SharedSecurity {
          --security-primary: #093a5b;
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) translateX(0px);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          100% {
            transform: scale(1.2);
            opacity: 0;
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes draw-line {
          from {
            stroke-dashoffset: 1000;
          }
          to {
            stroke-dashoffset: 0;
          }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        /* Hover Effects */
        .group:hover .group-hover\:scale-105 {
          transform: scale(1.05);
        }

        /* Responsive Adjustments */
        @media (max-width: 640px) {
          .SharedSecurity {
            padding-left: 1rem;
            padding-right: 1rem;
          }
        }

        @media (min-width: 768px) and (max-width: 1024px) {
          .SharedSecurity {
            padding-left: 2rem;
            padding-right: 2rem;
          }
        }

        /* Smooth Scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb {
          background: var(--security-primary);
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #0a4a6e;
        }

        /* Selection Style */
        ::selection {
          background: var(--security-primary);
          color: white;
        }

        /* Focus Styles */
        a:focus-visible,
        button:focus-visible {
          outline: 2px solid var(--security-primary);
          outline-offset: 2px;
          border-radius: 12px;
        }
      `}</style>
    </section>
  );
};

export default SharedSecurity;
